import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, doc,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  toDateStr, timeStrToDate, dateToTimeStr, formatTime,
  COMMON_ROLES, COMMON_ZONES,
} from './scheduleUtils';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';

/**
 * CreateShiftModal
 * Used for both creating new shifts and editing existing ones.
 *
 * Props:
 *   isOpen        — boolean
 *   onClose       — fn
 *   restaurantId  — string
 *   scheduleId    — string (weekStart "YYYY-MM-DD")
 *   employees     — [{ id, firstName, lastName, position }]
 *   prefillDate   — "YYYY-MM-DD" | null
 *   prefillEmployeeId — string | null
 *   editShift     — shift object | null (if editing)
 *   user          — portal user object
 */
const CreateShiftModal = ({
  isOpen, onClose, onSaved, onDelete, restaurantId, scheduleId, employees,
  tableZones = [], prefillDate, prefillEmployeeId, editShift, user,
  notifyOnPublish = true,
}) => {
  const [date, setDate]           = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole]           = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime]     = useState('17:00');
  const [zone, setZone]           = useState('');
  const [floorZone, setFloorZone] = useState(null);   // number | null — synced to POS currentZone on clock-in
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (editShift) {
      setDate(editShift.date || '');
      setEmployeeId(editShift.employeeId || '');
      setRole(editShift.role || '');
      setStartTime(editShift.startTime ? dateToTimeStr(editShift.startTime.toDate()) : '09:00');
      setEndTime(editShift.endTime ? dateToTimeStr(editShift.endTime.toDate()) : '17:00');
      setZone(editShift.zone || '');
      setFloorZone(editShift.floorZone ?? null);
      setNotes(editShift.notes || '');
    } else {
      setDate(prefillDate || '');
      setEmployeeId(prefillEmployeeId || '');
      const emp = employees.find(e => e.id === prefillEmployeeId);
      setRole(emp?.position && COMMON_ROLES.includes(emp.position) ? emp.position : '');
      setStartTime('09:00');
      setEndTime('17:00');
      setZone('');
      setFloorZone(null);
      setNotes('');
    }
    setError('');
  }, [isOpen, editShift, prefillDate, prefillEmployeeId]);

  if (!isOpen) return null;

  const selectedEmployee = employees.find(e => e.id === employeeId);

  const handleSave = async () => {
    if (!date) return setError('Please select a date.');
    if (!role)  return setError('Please select a role.');
    if (!startTime || !endTime) return setError('Please set start and end times.');

    const startDate = timeStrToDate(date, startTime);
    const endDate   = timeStrToDate(date, endTime);
    if (endDate <= startDate) return setError('End time must be after start time.');

    setSaving(true);
    setError('');

    const byName = `${user.firstName} ${user.lastName}`;

    try {
      const shiftData = {
        employeeId:   employeeId || null,
        employeeName: selectedEmployee
          ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
          : '',
        role,
        startTime: Timestamp.fromDate(startDate),
        endTime:   Timestamp.fromDate(endDate),
        date,
        zone: zone || null,
        floorZone: floorZone ?? null,
        notes,
        isOpenShift: !employeeId,
        notifyOnPublish,
        updatedAt:   serverTimestamp(),
        updatedBy:   user.accountId,
        updatedByName: byName,
      };

      if (editShift) {
        const before = {
          employeeId: editShift.employeeId,
          role: editShift.role,
          date: editShift.date,
          startTime: formatTime(editShift.startTime),
          endTime: formatTime(editShift.endTime),
        };
        await updateDoc(
          doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts', editShift.id),
          shiftData
        );
        await addDoc(
          collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
          { action: 'shift_updated', shiftId: editShift.id, by: user.accountId, byName, at: serverTimestamp(), before, after: { employeeId, role, date } }
        );
        onSaved?.({ type: 'shift_updated', shiftId: editShift.id });
      } else {
        const newRef = await addDoc(
          collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts'),
          { ...shiftData, status: 'scheduled', createdAt: serverTimestamp() }
        );
        await addDoc(
          collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
          { action: 'shift_created', shiftId: newRef.id, by: user.accountId, byName, at: serverTimestamp(), before: null, after: { employeeId, role, date } }
        );
        onSaved?.({ type: 'shift_created', shiftId: newRef.id });
      }

      onClose();
    } catch (err) {
      setError('Failed to save shift. Try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90dvh]">
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <h3 className="text-base font-semibold text-neutral-800">
            {editShift ? 'Edit Shift' : 'New Shift'}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>

          {/* Employee */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Employee</label>
            <select
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
            >
              <option value="">Open Shift (unassigned)</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}{emp.position ? ` — ${emp.position}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Role <span className="text-red-500">*</span></label>
            <div className="flex flex-wrap gap-2">
              {COMMON_ROLES.map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setRole(r);
                    if (r !== 'Server') { setZone(''); setFloorZone(null); }
                  }}
                  className={`text-sm px-3 py-1 rounded-full border font-medium transition-colors ${
                    role === r
                      ? 'bg-neutral-800 text-white border-neutral-800'
                      : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Times */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-500 mb-1">Start</label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-500 mb-1">End</label>
              <input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
          </div>

          {/* Zone — Server only */}
          {role === 'Server' && (
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Area (optional)</label>
            <div className="flex gap-2">
              {COMMON_ZONES.map(z => (
                <button
                  key={z}
                  type="button"
                  onClick={() => setZone(zone === z ? '' : z)}
                  className={`text-sm px-3 py-1 rounded-full border font-medium transition-colors ${
                    zone === z
                      ? 'bg-neutral-800 text-white border-neutral-800'
                      : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
                  }`}
                >
                  {z}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Floor Zone — only shown when the restaurant has numbered zones on tables */}
          {role === 'Server' && tableZones.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-neutral-500 mb-1">
                Floor Zone (optional)
                <span className="ml-1.5 text-xs text-neutral-400 font-normal">auto-assigns on clock-in</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {tableZones.map(z => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => setFloorZone(floorZone === z ? null : z)}
                    className={`text-sm px-3 py-1 rounded-full border font-medium transition-colors ${
                      floorZone === z
                        ? 'bg-brand-blue text-white border-brand-blue'
                        : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
                    }`}
                  >
                    Zone {z}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-500 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Cover the patio"
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-between gap-2 shrink-0">
          <div>
            {editShift && onDelete && (
              <button
                onClick={onDelete}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40"
              >
                Delete Shift
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : editShift ? 'Save Changes' : 'Create Shift'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateShiftModal;
